import React, { useEffect, useState } from 'react';
import { Progress } from '@mantine/core';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslate } from '@tolgee/react';
import { Typography } from '@/components/Atoms';
import CustomSkeleton from '@/components/Common/CustomSkeleton';
import { IUserDataCommandCenterProps } from '@/types/Achievements';
import { OtherAccountsResponseType } from '@/types/Inventory';
import { numberSeparator } from '@/utils/numberSeparator';
import LoginSection from '../../LoginSection';
import inventoryApi from '@/api/services/inventory';
import styles from './index.module.scss'; // Import CSS module
import useAuthStatuses from '@/hooks/authentication/useAuthStatuses';

const UserDataSection = (props: { userData: IUserDataCommandCenterProps }) => {
  const { t } = useTranslate('commandCenter');
  const { userData } = props;
  const { pathname } = useRouter();
  const { isLogin } = useAuthStatuses();
  const isCommandCenter = pathname == '/command-center';
  const [otherAccounts, setOtherAccounts] = useState<OtherAccountsResponseType | null>(null);

  const getUserOtherAccounts = () => {
    inventoryApi.checkOtherAccount().then((res) => {
      setOtherAccounts(res.data);
    });
  };

  useEffect(() => {
    if (isLogin) {
      getUserOtherAccounts();
    }
  }, [isLogin]);

  function calculateXpPercentage(userXp, userMinXp, userMaxXp) {
    const xpProgress = ((userXp - userMinXp) / (userMaxXp - userMinXp)) * 100;
    return Math.min(Math.max(xpProgress, 0), 100);
  }
  const percentage = calculateXpPercentage(userData?.user_xp, userData?.user_min_xp, userData?.user_max_xp);
  const truncatedUserName = userData?.user_name?.length > 15 ? userData?.user_name.slice(0, 12) + '...' : userData?.user_name;

  const telegramId = otherAccounts && otherAccounts.data.find((otherAccount) => otherAccount.AccountType === 'telegram')?.AccountId;

  return (
    <>
      {isLogin ? (
        <div className={isLogin && isCommandCenter ? 'cursor-pointer w-full' : 'cursor-default'}>
          <div className='flex items-end gap-x-2'>
            <div className='w-[55px] h-[60px] rounded-full justify-center items-center z-[1]'>
              {userData?.user_badge_image ? (
                <div className='relative w-full h-full'>
                  <Image
                    src={userData?.user_badge_image}
                    layout='responsive'
                    objectFit='contain'
                    width={60}
                    height={60}
                    alt='user-badge'
                    className={`${styles.pulseScale} rounded-full z-[2] mt-auto aspect-square`} // Use the module class here
                  />
                </div>
              ) : (
                <CustomSkeleton height={60} width={60} circle />
              )}
            </div>
            <div className='flex flex-col w-[calc(100%-70px)]'>
              <Typography size='s14ToS12' className='lg:hidden flex items-center justify-start text-[#43E7DD] text-nowrap' isBold>
                {userData?.user_name}
              </Typography>
              <Typography size='s14ToS12' className='hidden lg:flex items-center justify-center text-[#43E7DD] text-nowrap' isBold>
                {truncatedUserName}
              </Typography>
              <div className='mt-2 -ml-10'>
                <Progress size={'lg'} value={percentage} radius={'lg'} color='#00AB55' className='bg-[#3B3F5C]' />
              </div>
              <Typography size='s12ToS12' className='text-end ms-auto flex'>
                {typeof userData?.user_xp !== undefined ? numberSeparator(+userData?.user_xp) : <CustomSkeleton height={18} width={40} />} Xp
              </Typography>
            </div>
          </div>
          <div className='flex flex-col'>
            <Typography size='s12ToS12'>
              {t('LEFT_SECTION.userRankSection.title')}: {numberSeparator(userData?.user_rank_level)}
            </Typography>
            <Typography size='s12ToS12'>
              {t('LEFT_SECTION.userRankSection.text')}: {userData?.uer_referral_code}
            </Typography>
            <Typography size='s12ToS12'>Telegram ID: {telegramId}</Typography>
          </div>
        </div>
      ) : (
        <LoginSection />
      )}
    </>
  );
};

export default UserDataSection;
